import { format, formatDistance as formatDistanceFromNow, parseISO } from 'date-fns';
import { clsx, ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind CSS classes with proper precedence
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** App-wide date display: dd-MM-yyyy (e.g. 06-06-2026). */
export const DISPLAY_DATE_FORMAT = 'dd-MM-yyyy';

/** App-wide datetime display: dd-MM-yyyy, h:mm a (e.g. 06-06-2026, 2:30 PM). */
export const DISPLAY_DATETIME_FORMAT = 'dd-MM-yyyy, h:mm a';

/** App-wide time display: h:mm a (e.g. 2:30 PM). */
export const DISPLAY_TIME_FORMAT = 'h:mm a';

function parseForDisplay(value: string | Date): Date {
  if (value instanceof Date) return value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return parseISO(value);
}

/**
 * Format date to readable string
 * @param date - Date string or Date object
 * @param formatStr - Date format string (default: dd-MM-yyyy)
 */
export function formatDate(date: string | Date | null | undefined, formatStr = DISPLAY_DATE_FORMAT): string {
  if (!date) return 'N/A';

  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, formatStr);
  } catch (error) {
    return 'Invalid date';
  }
}

/**
 * Format date and time to readable string
 * @param date - Date string or Date object
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  return formatDate(date, DISPLAY_DATETIME_FORMAT);
}

/**
 * Driving / elapsed duration as HH:MM (e.g. 02:30 for 2.5 hours).
 * Prefer seconds when available for accuracy.
 */
export function formatDurationHhMm(
  durationSecondsOrMinutes: number | null | undefined,
  unit: 'seconds' | 'minutes' = 'seconds',
): string {
  if (durationSecondsOrMinutes == null || Number.isNaN(durationSecondsOrMinutes)) {
    return '00:00';
  }
  const totalSeconds =
    unit === 'minutes'
      ? Math.max(0, Math.round(durationSecondsOrMinutes * 60))
      : Math.max(0, Math.round(durationSecondsOrMinutes));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Date-only display for lists and detail fields. */
export function formatDisplayDate(date: string | Date | null | undefined): string {
  if (!date) return '—';

  try {
    const dateObj = typeof date === 'string' ? parseForDisplay(date) : date;
    if (Number.isNaN(dateObj.getTime())) return '—';
    return format(dateObj, DISPLAY_DATE_FORMAT);
  } catch {
    return '—';
  }
}

/** Datetime display without seconds. */
export function formatLocaleDateTime(date: string | Date | null | undefined): string {
  if (!date) return '—';

  try {
    const dateObj = typeof date === 'string' ? parseForDisplay(date) : date;
    if (Number.isNaN(dateObj.getTime())) return '—';
    return format(dateObj, DISPLAY_DATETIME_FORMAT);
  } catch {
    return '—';
  }
}

/** Time-only value (HH:mm or HH:mm:ss) without seconds. */
export function formatTimeOfDay(value?: string | null): string {
  if (!value?.trim()) return '';

  const trimmed = value.trim();
  try {
    if (trimmed.includes('T')) {
      const d = parseISO(trimmed);
      if (Number.isNaN(d.getTime())) return trimmed;
      return format(d, DISPLAY_TIME_FORMAT);
    }

    const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
    if (!match) return trimmed;

    const d = new Date(1970, 0, 1, Number(match[1]), Number(match[2]));
    if (Number.isNaN(d.getTime())) return trimmed;
    return format(d, DISPLAY_TIME_FORMAT);
  } catch {
    return trimmed;
  }
}

/** Compact list date: d MMM yyyy (e.g. 27 Jun 2026). */
export const QUEUE_LIST_DATE_FORMAT = 'd MMM yyyy';

export function formatQueueListDate(date: string | Date | null | undefined): string | null {
  if (!date) return null;

  try {
    const dateObj = typeof date === 'string' ? parseForDisplay(date) : date;
    if (Number.isNaN(dateObj.getTime())) return null;
    return format(dateObj, QUEUE_LIST_DATE_FORMAT);
  } catch {
    return null;
  }
}

export function formatQueueListTime(
  dateOrDateTime?: string | null,
  timeOnly?: string | null,
): string | null {
  if (timeOnly?.trim()) {
    const formatted = formatTimeOfDay(timeOnly);
    return formatted || null;
  }

  if (!dateOrDateTime?.trim()) return null;

  try {
    if (dateOrDateTime.includes('T')) {
      const d = parseISO(dateOrDateTime);
      if (Number.isNaN(d.getTime())) return null;
      return format(d, DISPLAY_TIME_FORMAT);
    }
    return null;
  } catch {
    return null;
  }
}

export function formatQueueAge(iso?: string | null): string | null {
  if (!iso) return null;

  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return null;

  const mins = Math.max(0, Math.floor((Date.now() - at) / 60000));
  if (mins < 60) {
    const m = Math.max(1, mins);
    return `Age: ${m} min`;
  }

  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return `Age: ${hours} hour${hours === 1 ? '' : 's'}`;
  }

  const days = Math.floor(hours / 24);
  return `Age: ${days} day${days === 1 ? '' : 's'}`;
}

export function formatQueueListSubline(
  time: string | null,
  age?: string | null,
): string | null {
  if (time && age) return `${time} • ${age}`;
  return time ?? age ?? null;
}

/** Placement schedule: date + optional clock time. */
export function formatPlacementSchedule(
  date?: string | null,
  time?: string | null,
): string {
  const parts = [
    date ? formatDisplayDate(date) : null,
    time ? formatTimeOfDay(time) : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

function toValidDate(date: string | Date | null | undefined): Date | null {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) return null;
  return dateObj;
}

/** DD/MM/YYYY — MDR / portal / planning / vendor quote (pre-docket). */
export function formatDateDdMmYyyy(date: string | Date | null | undefined): string {
  const dateObj = toValidDate(date);
  if (!dateObj) return '—';
  try {
    return format(dateObj, 'dd/MM/yyyy');
  } catch {
    return '—';
  }
}

/** DD/MM/YYYY HH:mm — MDR / portal / planning / vendor quote timestamps. */
export function formatDateTimeDdMmYyyy(date: string | Date | null | undefined): string {
  const dateObj = toValidDate(date);
  if (!dateObj) return '—';
  try {
    return format(dateObj, 'dd/MM/yyyy HH:mm');
  } catch {
    return '—';
  }
}

/**
 * Format date to relative time (e.g., "2 hours ago")
 * @param date - Date string or Date object
 */
export function formatRelativeDate(date: string | Date | null | undefined): string {
  if (!date) return 'N/A';

  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return formatDistanceFromNow(dateObj, new Date(), { addSuffix: true });
  } catch (error) {
    return 'Invalid date';
  }
}

/**
 * Format currency value
 * @param amount - Amount to format
 * @param currency - Currency code (default: 'INR')
 * @param locale - Locale for formatting (default: 'en-IN')
 */
export function formatCurrency(
  amount: number | null | undefined,
  currency = 'INR',
  locale = 'en-IN'
): string {
  if (amount === null || amount === undefined) return 'N/A';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `₹${amount.toFixed(2)}`;
  }
}

/**
 * Format number with thousand separators
 * @param value - Number to format
 * @param decimals - Number of decimal places (default: 0)
 */
export function formatNumber(value: number | null | undefined, decimals = 0): string {
  if (value === null || value === undefined) return 'N/A';

  try {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  } catch (error) {
    console.error('Error formatting number:', error);
    return value.toString();
  }
}

/**
 * Format shipment status to display string
 * @param status - Status value
 */
export function formatStatus(status: string | null | undefined): string {
  if (!status) return 'N/A';

  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Get status badge color classes
 * @param status - Status value
 */
export function getStatusColor(status: string): string {
  const statusColors: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-200',
    picked_up: 'bg-blue-100 text-foreground',
    in_transit: 'bg-info/15 text-foreground',
    out_for_delivery: 'bg-primary/15 text-foreground',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    delayed: 'bg-yellow-100 text-yellow-800',
    exception: 'bg-orange-100 text-orange-800',
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-gray-100 text-gray-200',
    suspended: 'bg-red-100 text-red-800',
  };

  return statusColors[status.toLowerCase()] || 'bg-gray-100 text-gray-200';
}

/**
 * Format file size to human-readable string
 * @param bytes - File size in bytes
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Format distance
 * @param distance - Distance value
 * @param unit - Distance unit (default: 'miles')
 */
export function formatDistance(distance: number | null | undefined, unit = 'miles'): string {
  if (distance === null || distance === undefined) return 'N/A';

  return `${formatNumber(distance, 1)} ${unit}`;
}

/**
 * Format weight
 * @param weight - Weight value
 * @param unit - Weight unit (default: 'lbs')
 */
export function formatWeight(weight: number | null | undefined, unit = 'lbs'): string {
  if (weight === null || weight === undefined) return 'N/A';

  return `${formatNumber(weight, 2)} ${unit}`;
}

/**
 * Truncate text to specified length
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 */
export function truncate(text: string | null | undefined, maxLength = 50): string {
  if (!text) return '';

  if (text.length <= maxLength) return text;

  return `${text.slice(0, maxLength)}...`;
}

/**
 * Generate initials from name
 * @param name - Full name
 */
export function getInitials(name: string | null | undefined): string {
  if (!name) return 'N/A';

  const parts = name.trim().split(' ');

  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Validate email address
 * @param email - Email to validate
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate phone number (US format)
 * @param phone - Phone number to validate
 */
export function isValidPhone(phone: string): boolean {
  const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
  return phoneRegex.test(phone);
}

/**
 * Sleep/delay function
 * @param ms - Milliseconds to sleep
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Debounce function
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Download file from blob
 * @param blob - Blob data
 * @param filename - File name
 */
export function downloadFile(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Copy text to clipboard
 * @param text - Text to copy
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}

/**
 * Generate random ID
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

/**
 * Calculate percentage
 * @param value - Current value
 * @param total - Total value
 */
export function calculatePercentage(value: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
}

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 */
export function isEmpty(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}
